// 013A — Anatomía Patológica Solicitud (Anexo 1 MSP, SNS-MSP/HCU-form.013A/2021). Fase 7, Tanda 4: bloques A-I del
// papel (servicio y prioridad, estudio solicitado con casillas, resumen clínico en 5 campos, 5 diagnósticos con CIE y
// PRE/DEF, muestra, citología vaginal, biología molecular y recepción). Campos y decisiones en
// `bloquesAnatomiaPatologicaDatos.ts`. El bloque A lo resuelve `EncabezadoMspCampos`; el profesional (I) lo arma el
// servidor en el PDF firmado.
import { useMedplum } from '@medplum/react-hooks';
import type { ServiceRequest } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { AreaDictado } from './AreaDictado';
import { CATEGORIA_ANATOMIA_PATOLOGICA } from './categoriasDiagnosticas';
import { Bloque, CamposSimples } from './camposSimples';
import { MARCAS_PRE_DEF, type CampoSimple, type Valores } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca } from './MarcaDiagnostico';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_ANTICONCEPCION_CITOLOGIA,
  CAMPOS_BIOLOGIA_MOLECULAR_013A,
  CAMPOS_DESCRIPCION_CITOLOGIA,
  CAMPOS_ESTUDIO_013A,
  CAMPOS_HISTORIA_CITOLOGIA,
  CAMPOS_MATERIAL_CITOLOGIA,
  CAMPOS_MUESTRA_013A,
  CAMPOS_RECEPCION_013A,
  CAMPOS_RESUMEN_013A,
  CAMPOS_SERVICIO_013A,
  estadoInicial013A,
  paresDe013A,
  prioridadFhir013A,
  textoSolicitud013A,
  type Estado013A,
} from './bloquesAnatomiaPatologicaDatos';

const sinAreas = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo !== 'area');
const soloAreas = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo === 'area');

const CAMPOS_ESTUDIO_SIN_AREA = sinAreas(CAMPOS_ESTUDIO_013A);
const CAMPO_DESCRIPCION = soloAreas(CAMPOS_ESTUDIO_013A)[0]!;
const CAMPO_MUESTRA = CAMPOS_MUESTRA_013A[0]!;
const CAMPO_BIOLOGIA = CAMPOS_BIOLOGIA_MOLECULAR_013A[0]!;
const CAMPOS_DESCRIPCION_SIN_AREA = sinAreas(CAMPOS_DESCRIPCION_CITOLOGIA);
const CAMPO_MACROSCOPICA = soloAreas(CAMPOS_DESCRIPCION_CITOLOGIA)[0]!;

export function Formulario013A({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado013A>(() => estadoInicial013A());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '013A', t('formularios.f013a.titulo'), { servicio: estado.servicio.servicio });

  const area = (clave: string, valor: string, filas: number, etiqueta: string, id: string, poner: (v: string) => void): JSX.Element => (
    <AreaDictado key={clave} id={id} etiqueta={etiqueta} valor={valor} filas={filas} onChange={poner} />
  );
  const cambiar = <K extends keyof Estado013A>(clave: K, valores: Valores): void => setEstado({ ...estado, [clave]: valores });

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const reasonCode = estado.diagnosticos.filter((d) => d.diagnostico.texto.trim()).map((d) => codeDeDiagnostico(d.diagnostico));
      const notas = [
        ...CAMPOS_RESUMEN_013A.map((c) => ({ etiqueta: c.etiqueta, valor: (estado.resumen[c.clave] ?? '').trim() })),
        { etiqueta: 'Descripción', valor: (estado.estudio.descripcion ?? '').trim() },
      ]
        .filter((n) => n.valor)
        .map((n) => ({ text: `${n.etiqueta}: ${n.valor}` }));
      await medplum.createResource<ServiceRequest>({
        resourceType: 'ServiceRequest',
        status: 'active',
        intent: 'order',
        category: [CATEGORIA_ANATOMIA_PATOLOGICA],
        priority: prioridadFhir013A(estado.servicio.prioridad),
        code: { text: textoSolicitud013A(estado) },
        subject,
        encounter,
        authoredOn: new Date().toISOString(),
        reasonCode: reasonCode.length ? reasonCode : undefined,
        note: notas,
      });
      onGuardado(paresDe013A(estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f013a.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f013a.servicioTitulo')}>
        <CamposSimples prefijoId="f013a-srv" campos={CAMPOS_SERVICIO_013A} valores={estado.servicio} columnas={3} onChange={(v) => cambiar('servicio', v)} />
      </Bloque>

      <Bloque titulo={t('formularios.f013a.estudioTitulo')}>
        <CamposSimples prefijoId="f013a-est" campos={CAMPOS_ESTUDIO_SIN_AREA} valores={estado.estudio} columnas={4} onChange={(v) => cambiar('estudio', { ...estado.estudio, ...v })} />
        {area('descripcion', estado.estudio.descripcion ?? '', 3, CAMPO_DESCRIPCION.etiqueta, 'f013a-est-descripcion', (v) => cambiar('estudio', { ...estado.estudio, descripcion: v }))}
      </Bloque>

      <Bloque titulo={t('formularios.f013a.resumenTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f013a.resumenNota')}</p>
        {CAMPOS_RESUMEN_013A.map((c) => area(c.clave, estado.resumen[c.clave] ?? '', 3, c.etiqueta, `f013a-res-${c.clave}`, (v) => cambiar('resumen', { ...estado.resumen, [c.clave]: v })))}
      </Bloque>

      <Bloque titulo={t('formularios.f013a.diagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f013a.diagnosticoNota')}</p>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f013a.diagnostico')} ${i + 1}`}
            marcaLabel={t('formularios.f013a.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>

      <Bloque titulo={t('formularios.f013a.muestraTitulo')}>
        {area(CAMPO_MUESTRA.clave, estado.muestra.muestra ?? '', 4, CAMPO_MUESTRA.etiqueta, 'f013a-muestra', (v) => cambiar('muestra', { muestra: v }))}
      </Bloque>

      <Bloque titulo={t('formularios.f013a.citologiaTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f013a.citologiaNota')}</p>
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f013a.material')}</p>
        <CamposSimples prefijoId="f013a-mat" campos={CAMPOS_MATERIAL_CITOLOGIA} valores={estado.material} columnas={3} onChange={(v) => cambiar('material', v)} />
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f013a.anticoncepcion')}</p>
        <CamposSimples prefijoId="f013a-anti" campos={CAMPOS_ANTICONCEPCION_CITOLOGIA} valores={estado.anticoncepcion} columnas={4} onChange={(v) => cambiar('anticoncepcion', v)} />
        <CamposSimples prefijoId="f013a-hist" campos={CAMPOS_HISTORIA_CITOLOGIA} valores={estado.historia} columnas={4} onChange={(v) => cambiar('historia', v)} />
        <CamposSimples prefijoId="f013a-dcit" campos={CAMPOS_DESCRIPCION_SIN_AREA} valores={estado.descripcionCitologia} columnas={2} onChange={(v) => cambiar('descripcionCitologia', { ...estado.descripcionCitologia, ...v })} />
        {area(CAMPO_MACROSCOPICA.clave, estado.descripcionCitologia.descripcionMacroscopica ?? '', 3, CAMPO_MACROSCOPICA.etiqueta, 'f013a-dcit-macro', (v) => cambiar('descripcionCitologia', { ...estado.descripcionCitologia, descripcionMacroscopica: v }))}
      </Bloque>

      <Bloque titulo={t('formularios.f013a.biologiaTitulo')}>
        {area(CAMPO_BIOLOGIA.clave, estado.biologiaMolecular.biologiaMolecular ?? '', 3, CAMPO_BIOLOGIA.etiqueta, 'f013a-bio', (v) => cambiar('biologiaMolecular', { biologiaMolecular: v }))}
      </Bloque>

      <Bloque titulo={t('formularios.f013a.recepcionTitulo')}>
        <CamposSimples prefijoId="f013a-rec" campos={CAMPOS_RECEPCION_013A} valores={estado.recepcion} columnas={3} onChange={(v) => cambiar('recepcion', v)} />
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
